import type { AdvancedSettings, SchedulingRule, SchedulingRuleset } from '@/types/template';

/**
 * Adapter between the canonical contract `SchedulingRuleset` (stored on
 * `WizardState`) and the ergonomic flat `AdvancedSettings` view the Advanced
 * Settings form edits. The wizard persists the ruleset; the form works in flat
 * space and converts at its edge.
 *
 * This is the "thin UI-only mapper for genuinely-UI concerns" the
 * contracts-as-source-of-truth approach allows — binding a discriminated-union
 * rule array straight to form controls would be the wrong ergonomics.
 */

/** Default split-shift values used when a ruleset carries no `splitShifts` rule. */
const DEFAULT_ALLOW_SPLIT_SHIFTS = true;
const DEFAULT_MAX_SHIFTS_PER_DAY = 2;

/**
 * Flat `AdvancedSettings` → canonical `SchedulingRuleset`. Optional numeric
 * rules (rest break, min days off, max consecutive) are emitted only when set
 * and positive; the split-shifts rule is always emitted (disabled ⇒
 * `maxShiftsPerDay: 1`).
 */
export function toSchedulingRuleset(settings: AdvancedSettings): SchedulingRuleset {
  const rules: SchedulingRule[] = [];

  if (settings.overrideRestBreak !== undefined && settings.overrideRestBreak > 0) {
    rules.push({
      kind: 'restBreakBetweenShifts',
      enabled: true,
      hours: settings.overrideRestBreak,
    });
  }

  const allowSplitShifts = settings.allowSplitShifts;
  rules.push({
    kind: 'splitShifts',
    enabled: allowSplitShifts,
    maxShiftsPerDay: allowSplitShifts
      ? (settings.maxSplitShiftsPerDay ?? DEFAULT_MAX_SHIFTS_PER_DAY)
      : 1,
  });

  if (settings.minDaysOffPerWeek !== undefined && settings.minDaysOffPerWeek > 0) {
    rules.push({
      kind: 'minimumDaysOff',
      enabled: true,
      daysOffPerWeek: settings.minDaysOffPerWeek,
      requireConsecutiveDaysOff: settings.consecutiveDaysOff ?? false,
    });
  }

  if (settings.maxConsecutiveDaysWorked !== undefined && settings.maxConsecutiveDaysWorked > 0) {
    rules.push({
      kind: 'maxConsecutiveDays',
      enabled: true,
      maxDays: settings.maxConsecutiveDaysWorked,
    });
  }

  return { rules };
}

/**
 * Canonical `SchedulingRuleset` → flat `AdvancedSettings`. Absent optional rules
 * stay `undefined`; the split-shifts fields fall back to sensible defaults when
 * the ruleset carries no `splitShifts` rule.
 */
export function toAdvancedSettings(ruleset: SchedulingRuleset): AdvancedSettings {
  const settings: AdvancedSettings = {
    allowSplitShifts: DEFAULT_ALLOW_SPLIT_SHIFTS,
    maxSplitShiftsPerDay: DEFAULT_MAX_SHIFTS_PER_DAY,
  };

  for (const rule of ruleset.rules) {
    switch (rule.kind) {
      case 'restBreakBetweenShifts':
        settings.overrideRestBreak = rule.hours;
        break;
      case 'splitShifts':
        settings.allowSplitShifts = rule.enabled;
        settings.maxSplitShiftsPerDay = rule.maxShiftsPerDay;
        break;
      case 'minimumDaysOff':
        settings.minDaysOffPerWeek = rule.daysOffPerWeek;
        settings.consecutiveDaysOff = rule.requireConsecutiveDaysOff;
        break;
      case 'maxConsecutiveDays':
        settings.maxConsecutiveDaysWorked = rule.maxDays;
        break;
    }
  }

  return settings;
}
